import { ListMembersRequest, ListMembersRequestLite } from '../types/requests/ListMembers';
import {
	ConvertListMembersResponse,
	ListMembersResponse,
	Member,
} from '../types/response/ListMembersResponse';
import AppConfig from '../../../lib/AppConfig';
import { ConvertMemberTemplate, MemberTemplate } from '../types/MemberTemplate';
import { ConvertCreateMemberRequest, CreateMemberRequest } from '../types/requests/CreateMember';
import { cleanApiError, handleResponse } from '../../../utils/handleResponse';
import { buildQuery, getAPIHeaders, QueryParams } from '../../../utils/helpers';
import { ConvertCreateClientResponse, CreateClientResponse } from '../types/response/CreateClient';
import { ClientAddress } from '../types/ClientAddress';
import { CreateUserRequest } from '../types/requests/CreateUser';
import { AddFamilyMember, FamilyMember } from '../types/FamilyMember';
import {
	TAcceptTransfer,
	TActivateMember,
	TClosureRequest,
	TProposeTransferRequest,
	TReactivateRequest,
	TTransferAction,
	UpdateMemberRequest,
} from '../types/requests/Member';
import { UpdateAddress } from '../types/requests/Address';
import { ClientTransactionsResponse } from '../types/response/Transactions';
import { SearchClientsRequest } from '../types/requests/Search';
import { SearchClientsResponse } from '../types/response/Search';
import { MemberResponseLite } from '../types/response/MemberLite';
import { MemberSummaryResponse } from '../types/response/Summary';
import { GenerateStatementPayload } from '../types/requests/Statement';
import { CloseTemplateResponse } from '../types/response/Close';
import { Office } from '../../Office/types/response/Office';
import { apiFetch } from '../../../lib/apiFetch';

export namespace MembersRepository {
	// listMembers
	export async function listMembers(request: ListMembersRequest): Promise<ListMembersResponse> {
		// Implementation can change
		try {
			const endPoint = `/fineract-provider/api/v1/clients`;
			const pagination = `${request.offset ? `offset=${request.offset}` : ''}${request.limit ? `&limit=${request.limit}` : ''}`;
			const displayName = `${request.displayName ? `&displayName=${request.displayName ?? ''}` : ''}`;
			const orderBy = `&orderBy=${request.orderBy ?? 'accountNo'}`;
			const sorttBy = `&sortBy=${request.sortBy ?? 'DESC'}`;
			const url = `${AppConfig.api_url}${endPoint}?${pagination}${displayName}${orderBy}${sorttBy}`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			const data = await handleResponse(response);

			return ConvertListMembersResponse.toListMembersResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listMembersLite retrieves small deetails about members
	export async function listMembersLite(
		request: ListMembersRequestLite,
	): Promise<MemberResponseLite> {
		// Implementation can change
		const qs = buildQuery(request as QueryParams);
		const url = `${AppConfig.api_url}/gmapis/members${qs ? `?${qs}` : ''}`;
		return apiFetch(url);
	}

	// getMemberSummary gets member summary
	export async function getMemberSummary(params?: QueryParams): Promise<MemberSummaryResponse> {
		const qs = buildQuery(params);
		const url = `${AppConfig.api_url}/gmapis/members/summary${qs ? `?${qs}` : ''}`;
		return apiFetch(url);
	}

	// listSearchClients
	export async function listSearchClients(
		request: ListMembersRequest,
	): Promise<ListMembersResponse> {
		try {
			const endPoint = `/fineract-provider/api/v2/clients/search`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const body = {
				request: {
					text: request.displayName,
				},
				page: 0,
				size: request.limit ?? 50,
			};

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
			});

			const data: SearchClientsResponse = await handleResponse(response);

			// Transform the response into the expected format, if necessary
			return {
				totalFilteredRecords: data.totalElements,
				pageItems: data.content,
			};
		} catch (error: any) {
			console.error('Search request failed', error);
			throw cleanApiError(error);
		}
	}

	// searchClients
	export async function searchClients(
		request: SearchClientsRequest,
	): Promise<SearchClientsResponse> {
		try {
			const endPoint = `/fineract-provider/api/v2/clients/search`;
			const url = `${AppConfig.api_url}${endPoint}`;

			const body = {
				request: {
					text: request.text,
				},
				page: request.page ?? 0,
				size: request.size ?? 50,
			};

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
			});

			const data = await handleResponse(response);

			// Transform the response into the expected format, if necessary
			return data;
		} catch (error: any) {
			console.error('Search request failed', error);
			throw cleanApiError(error);
		}
	}

	// getTemplate
	export async function getTemplate(): Promise<MemberTemplate> {
		// Implementation can change
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/template`;

			const response = await fetch(url, {
				headers: await getAPIHeaders(),
			});

			if (!response.ok) {
				throw await response.json();
			}

			const data = await handleResponse(response);

			return ConvertMemberTemplate.toMemberTemplate(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// create client
	export async function createClient(request: CreateMemberRequest): Promise<CreateClientResponse> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: ConvertCreateMemberRequest.createMemberRequestToJson(request),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// retrieves a client
	export async function getClient(clientId: number | string): Promise<Member> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// getClientImage
	export async function getClientImage(clientId: string | number): Promise<string> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}/images`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// addClientImage
	export async function addClientImage(clientId: string | number, data: FormData): Promise<void> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}/images`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				method: 'POST',
				body: data,
				headers,
			});

			await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listAddresses
	export async function listAddresses(clientId: string | number): Promise<ClientAddress[]> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/client/${clientId}/addresses`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// createSelfServiceUser
	export async function createSelfServiceUser(req: CreateUserRequest): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/users`;
			const headers = await getAPIHeaders();

			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(req),
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	// listFamilyMembers
	export async function listFamilyMembers(clientId: string | number): Promise<FamilyMember[]> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}/familymembers`;

			const headers = await getAPIHeaders();

			const response = await fetch(url, {
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// activateMember
	export async function activateMember(
		clientId: string | number,
		req: TActivateMember,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=activate`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// closeMemberTemplate
	export async function closeMemberTemplate(): Promise<CloseTemplateResponse> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/template?commandParam=close`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// closeMember
	export async function closeMember(clientId: string | number, req: TClosureRequest): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=close`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// transferMemberTemplate
	export async function transferMemberTemplate(): Promise<Office[]> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/offices`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				headers,
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// transferMember
	export async function transferMember(
		clientId: string | number,
		req: TProposeTransferRequest,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=proposeTransfer`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// withdrawTransfer
	export async function withdrawTransfer(
		clientId: string | number,
		req: TTransferAction,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=withdrawTransfer`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// rejectTransfer
	export async function rejectTransfer(
		clientId: string | number,
		req: TTransferAction,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=rejectTransfer`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// acceptTransfer
	export async function acceptTransfer(
		clientId: string | number,
		req: TAcceptTransfer,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=acceptTransfer`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// reactivate
	export async function reactivate(
		clientId: string | number,
		req: TReactivateRequest,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${clientId}?command=reactivate`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: JSON.stringify(req),
			});

			return await handleResponse(response);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// updateMember
	export async function updateMember(
		memberId: string | number,
		request: UpdateMemberRequest,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${memberId}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'PUT',
				body: JSON.stringify(request),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// addFamilyMember
	export async function addFamilyMember(
		memberId: string | number,
		request: AddFamilyMember,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${memberId}/familymembers`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(request),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// updateFamilyMember
	export async function updateFamilyMember(
		memberId: string | number,
		familyMemberId: string | number,
		request: AddFamilyMember,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${memberId}/familymembers/${familyMemberId}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'PUT',
				body: JSON.stringify(request),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// removeFamilyMember
	export async function removeFamilyMember(
		memberId: string | number,
		familyMemberId: string | number,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${memberId}/familymembers/${familyMemberId}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'DELETE',
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// addAddress
	export async function addAddress(
		memberId: string | number,
		addressType: string | number,
		req: UpdateAddress,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/client/${memberId}/addresses?type=${addressType}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(req),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// updateAddress
	export async function updateAddress(
		memberId: string | number,
		addressType: string | number,
		req: UpdateAddress,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/client/${memberId}/addresses?type=${addressType}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'PUT',
				body: JSON.stringify(req),
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// removeAddress
	export async function removeAddress(
		memberId: string | number,
		addressId: string | number,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/client/${memberId}/addresses/${addressId}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'DELETE',
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// removeDirector
	export async function removeDirector(
		memberId: string | number,
		familyMemberId: string | number,
	): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${memberId}/familymembers/${familyMemberId}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'DELETE',
				headers,
			});

			const data = await handleResponse(response);

			return ConvertCreateClientResponse.toCreateClientResponse(data);
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// listClientTransactions
	export async function listClientTransactions(
		memberId: string | number,
		{ limit, offset }: { limit?: number; offset?: number },
	): Promise<ClientTransactionsResponse> {
		try {
			const pagination = `${offset ? `offset=${offset}` : ''}${limit ? `&limit=${limit}` : ''}`;

			const url = `${AppConfig.api_url}/fineract-provider/api/v1/clients/${memberId}/transactions?${pagination}`;

			const headers = await getAPIHeaders();
			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'GET',
				headers,
			});

			const data = await handleResponse(response);

			return data;
		} catch (error: any) {
			throw cleanApiError(error);
		}
	}

	// createIprsRequest
	export async function createIprsRequest(req: any): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/query_iprs`;
			const headers = await getAPIHeaders();

			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify(req),
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	// triggerPinReset
	export async function triggerPinReset(phone: string): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/resetPin`;
			const headers = await getAPIHeaders();

			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify({ phone: phone }),
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	// updateMsisdn
	export async function updateMsisdn(phone: string, memberId: number): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/updateMsisdn`;
			const headers = await getAPIHeaders();

			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify({ msisdn: phone, memberId }),
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}

	// requestStatement
	export async function requestStatement(req: GenerateStatementPayload): Promise<any> {
		try {
			const url = `${AppConfig.api_url}/gmapis/member/statements/generate`;
			const headers = await getAPIHeaders();

			headers.set('Content-Type', 'application/json');

			const response = await fetch(url, {
				method: 'POST',
				body: JSON.stringify({
					member_id: req.clientId,
					start_date: req.startDate,
					end_date: req.endDate,
					email: req.email,
					account_type: req.accountType,
					savings_account_no: req.savingsAccountNos,
					loans_account_no: req.loanAccountNos,
				}),
				headers,
			});

			return await handleResponse(response);
		} catch (error) {
			throw cleanApiError(error);
		}
	}
}
